/**
 * Splits an email HTML body into the visible "new" content and the collapsible
 * "hidden" content (quoted history + broker signature). Mirrors how Gmail shows
 * the reply with a `•••` toggle that expands the rest.
 *
 * Detection:
 *  - Quoted history: `.gmail_quote`, `.gmail_attr`, `blockquote`, `#divRplyFwdMsg`,
 *    Outlook divider (div with `border-top: solid` inline style), plain-text
 *    "On ... wrote:" attribution lines.
 *  - Signature: any node whose text content starts with the broker's
 *    `footerStartPhrase` (supplied from the `broker_email_footers` collection).
 *
 * Once a hidden boundary is found, every following sibling at the same level
 * (and their descendants) is moved into the hidden bucket.
 */
export interface SplitEmail {
    visible: string
    hidden: string
}

const ON_WROTE_RE = /^\s*On\s.+\swrote:\s*$/i
const OUTLOOK_DIVIDER_RE = /border-top\s*:\s*solid/i
// Quote wrapper used by many automated replies: a vertical bar + left padding
// around the quoted block. Matches `border-left: 1px solid #ccc; padding-left: 1em;`.
const QUOTE_WRAPPER_RE = /border-left\s*:\s*[^;]*\bsolid\b/i
// RFC 3676 signature separator: a line containing just `--` (two dashes).
// Many Outlook/Gmail clients emit it as its own `<p>` or `<div>`.
const SIG_SEPARATOR_RE = /^\s*(--|—)\s*$/

// Common English sign-offs. When a paragraph contains ONLY one of these
// (optionally followed by `,` `.` or `!`), treat it as the start of the
// signature block — everything after is author contact info.
const SIGN_OFFS = new Set([
    'thanks',
    'thank you',
    'thanks again',
    'thanks so much',
    'best',
    'best regards',
    'best wishes',
    'kind regards',
    'warm regards',
    'regards',
    'sincerely',
    'cheers',
    'respectfully',
    'talk soon',
    'take care',
])

/** Strips the Markdown markers we've seen in footer phrases extracted upstream:
 * bold/italic (`**x**`, `__x__`, `*x*`, `_x_`), inline code (`` `x` ``), strike (`~~x~~`)
 * and link syntax `[text](url)` → `text`. Keeps the inner literal so the phrase
 * still matches plain-text rendered in the email DOM. */
function stripMarkdown(s: string): string {
    return s
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/(\*\*|__|~~)(.+?)\1/g, '$2')
        .replace(/(^|[^*_])([*_])([^*_\s][^*_]*?)\2(?!\w)/g, '$1$3')
        .replace(/`([^`]+)`/g, '$1')
}

/**
 * Normalize for comparison: strip markdown, drop pipes + ALL whitespace, lowercase.
 * Whitespace is stripped (not just collapsed) because `textContent` joins
 * inline-adjacent elements without a separator — e.g. `<span>buzz</span><span>
 * operational support</span>` yields `"buzzoperational support"`, which would
 * never match a phrase like `"buzz operational support"` under a space-
 * preserving compare. Pipes (literal `|` and Markdown-escaped `\|`) are also
 * stripped because footer phrases are sometimes stored upstream as a Markdown
 * table row (`| … | buzz \| … |`), and the rendered email DOM itself often
 * has a literal `|` between signature fields (`(470) … | buzz@…`). Stripping
 * on both sides keeps them aligned.
 */
function normalizeText(s: string): string {
    return (
        stripMarkdown(s)
            .replace(/\\\|/g, '')
            .replace(/\|/g, '')
            // RFC 3676 sig separator leaks into upstream-extracted footer phrases
            // as a literal leading `--` (often followed by a newline). Strip it so
            // phrases like `--\n| 31 Navasota St …` match the table element's own
            // textContent, which starts at `31 Navasota St`.
            .replace(/^\s*--+\s*/, '')
            .replace(/\s+/g, '')
            .toLowerCase()
    )
}

/**
 * Returns the earliest element in document order that marks a boundary.
 * Signatures often live above quoted history, so we collect every candidate
 * and pick the one that comes first — the one further up wins because
 * everything below it is already hidden by the boundary move anyway.
 *
 * `footerFirstLineNormalized` is a fallback for phrases that span multiple
 * DOM blocks (e.g. `"Thanks,\nJustin Cahill"` rendered as two separate `<p>`s,
 * where neither element's textContent starts with the full phrase). We match
 * just the first line (exact equality) as a secondary signal.
 */
function findBoundaryElement(root: Element, footerPhraseNormalized: string | null, footerFirstLineNormalized: string | null): Element | null {
    const candidates: Element[] = []

    for (const el of Array.from(root.querySelectorAll<HTMLElement>('.gmail_quote, .gmail_attr, blockquote, #divRplyFwdMsg'))) {
        candidates.push(el)
    }

    // `#Signature` is Outlook's sig wrapper — but some authors paste the ENTIRE
    // body inside it, making the "signature" the only content. Only treat it as
    // a boundary when real content precedes it in the body; otherwise we'd hide
    // the whole email and swap into showing everything unsplit.
    for (const el of Array.from(root.querySelectorAll<HTMLElement>('#Signature'))) {
        let prev: ChildNode | null = el.previousSibling
        let hasContentBefore = false
        while (prev) {
            // Skip comment nodes — they carry no visible content and shouldn't
            // influence whether the `#Signature` wrapper counts as a boundary.
            if (prev.nodeType === Node.COMMENT_NODE) {
                prev = prev.previousSibling
                continue
            }
            if (prev.nodeType === Node.TEXT_NODE && (prev.textContent ?? '').trim() !== '') {
                hasContentBefore = true
                break
            }
            if (prev.nodeType === Node.ELEMENT_NODE) {
                const pel = prev as Element
                const hasMedia = !!pel.querySelector('img, table, hr')
                const txt = (pel.textContent ?? '').replace(/[\s\u00a0]+/g, '')
                if (hasMedia || txt !== '') {
                    hasContentBefore = true
                    break
                }
            }
            prev = prev.previousSibling
        }
        if (hasContentBefore) candidates.push(el)
    }

    for (const div of Array.from(root.querySelectorAll<HTMLElement>('div[style]'))) {
        const style = div.getAttribute('style') || ''
        if (OUTLOOK_DIVIDER_RE.test(style)) candidates.push(div)
        if (QUOTE_WRAPPER_RE.test(style) && /padding-left/i.test(style)) candidates.push(div)
    }

    // Sign-offs are a low-confidence heuristic — a lone "Thanks," can appear
    // mid-body (acknowledging before asking a question). Collect them separately
    // and only use as a fallback when no authoritative boundary exists.
    const signOffFallbacks: Element[] = []
    for (const el of Array.from(root.querySelectorAll<HTMLElement>('p, div, span'))) {
        // Collapse whitespace first — some clients wrap the attribution line
        // around a bolded name, producing `"On …,\n<strong>Name</strong>\n<email> wrote:"`,
        // which the anchored regex won't match otherwise (`.` excludes `\n`).
        const collapsed = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
        if (ON_WROTE_RE.test(collapsed)) candidates.push(el)
        if (SIG_SEPARATOR_RE.test(collapsed)) candidates.push(el)
        const signoffKey = collapsed.replace(/[,.!]+$/, '').toLowerCase()
        if (signoffKey && SIGN_OFFS.has(signoffKey)) {
            const parentCollapsed = (el.parentElement?.textContent ?? '').replace(/\s+/g, ' ').trim()
            if (parentCollapsed !== collapsed) signOffFallbacks.push(el)
        }
    }

    if (footerPhraseNormalized) {
        for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
            const text = normalizeText(el.textContent ?? '')
            if (!text) continue
            if (!text.startsWith(footerPhraseNormalized)) continue
            const parentText = normalizeText(el.parentElement?.textContent ?? '')
            if (parentText === text) continue
            candidates.push(el)
        }
    }

    // First-line fallback: when the phrase spans several DOM blocks (`Thanks,`
    // in one `<p>` and `Justin Cahill` in the next), no single element's text
    // starts with the full phrase. Match the first-line token exactly instead.
    if (footerFirstLineNormalized && footerFirstLineNormalized !== footerPhraseNormalized) {
        for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
            const text = normalizeText(el.textContent ?? '')
            if (text !== footerFirstLineNormalized) continue
            // Prefer the innermost element so we cut cleanly (outer containers
            // that happen to wrap only this paragraph would have identical text).
            const parentText = normalizeText(el.parentElement?.textContent ?? '')
            if (parentText === text) continue
            candidates.push(el)
        }
    }

    // Sign-off is last-resort: only use if no authoritative boundary was found.
    const pool = candidates.length > 0 ? candidates : signOffFallbacks
    if (pool.length === 0) return null
    // Pick the candidate that appears first in document order.
    const earliest = pool.reduce((acc, el) => (acc.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING ? el : acc))
    // Promote upward through single-purpose wrappers so decorative container
    // elements (the `border-left` quote frame, a `<div dir="ltr">` wrapper, etc.)
    // are hidden along with the boundary instead of staying as empty shells.
    return promoteBoundary(earliest)
}

function promoteBoundary(boundary: Element): Element {
    let current = boundary
    while (current.parentElement) {
        const parent = current.parentElement
        if (parent === parent.ownerDocument?.body) break
        // Only promote when the parent wraps nothing but this element (ignoring
        // pure-whitespace siblings). Otherwise we'd swallow visible pre-boundary text.
        const siblings = Array.from(parent.childNodes)
        const nonWhitespaceSiblings = siblings.filter((n) => n !== current && (n.nodeType !== Node.TEXT_NODE || (n.textContent ?? '').trim() !== ''))
        if (nonWhitespaceSiblings.length > 0) break
        current = parent
    }
    return current
}

/**
 * Walks from the boundary up to `root`. At the boundary's own level it moves
 * the boundary element and every following sibling into `hiddenRoot`. At each
 * ancestor level only the siblings *after* the ancestor are moved — the
 * ancestor itself stays visible, because it still contains the pre-boundary
 * content.
 */
function extractHidden(root: Element, boundary: Element, hiddenRoot: Element): void {
    let current: Element | null = boundary
    let includeCurrent = true
    while (current && current !== root) {
        const parent: Element | null = current.parentElement
        let sibling: ChildNode | null = includeCurrent ? current : current.nextSibling
        while (sibling) {
            const next: ChildNode | null = sibling.nextSibling
            hiddenRoot.appendChild(sibling)
            sibling = next
        }
        includeCurrent = false
        current = parent
    }
}

/**
 * Drops `<div>` / `<p>` wrappers whose only content is whitespace or nbsp —
 * no text, no media. Outlook-style emails stamp these everywhere for vertical
 * padding (each carries a `margin-top:1em; margin-bottom:1em`), and once a
 * message is rendered in our narrow bubble they stack into visible gaps both
 * above and below the real content. Runs before boundary detection so the
 * stripped wrappers don't pollute the `promoteBoundary` sibling scan either.
 */
function stripEmptyBlocks(root: Element): void {
    for (const el of Array.from(root.querySelectorAll('div, p'))) {
        if (el.querySelector('img, table, hr, video, iframe')) continue
        const text = (el.textContent ?? '').replace(/[\s\u00a0]+/g, '')
        if (text !== '') continue
        el.remove()
    }
}

/**
 * Strip trailing whitespace-only / empty-paragraph nodes from `parent`. Outlook
 * emails often pad the body with `<p><o:p>&nbsp;</o:p></p>` filler paragraphs
 * that sit right before the signature — once the signature is collapsed the
 * filler shows up as an empty gap above the `•••` toggle. Preserves trailing
 * `<br>`, `<img>`, `<hr>`, `<table>` since those are intentional content.
 */
function trimTrailingEmpty(parent: Element): void {
    let last: ChildNode | null = parent.lastChild
    while (last) {
        const prev: ChildNode | null = last.previousSibling
        if (last.nodeType === Node.TEXT_NODE) {
            if ((last.textContent ?? '').replace(/[\s\u00a0]+/g, '') === '') {
                parent.removeChild(last)
                last = prev
                continue
            }
            break
        }
        if (last.nodeType === Node.ELEMENT_NODE) {
            const el = last as Element
            const tag = el.tagName.toLowerCase()
            if (tag === 'br' || tag === 'img' || tag === 'hr' || tag === 'table') break
            const hasMedia = !!el.querySelector('img, hr, table, video, iframe')
            const textOnly = (el.textContent ?? '').replace(/[\s\u00a0]+/g, '') === ''
            if (!hasMedia && textOnly) {
                parent.removeChild(last)
                last = prev
                continue
            }
            break
        }
        break
    }
}

export function splitEmailBody(html: string, footerStartPhrase: string | null): SplitEmail {
    const doc = new DOMParser().parseFromString(html || '', 'text/html')
    const body = doc.body
    stripEmptyBlocks(body)
    // Snapshot AFTER the empty-block strip — this is the fallback we render
    // when boundary detection mis-fires. Renamed from "original" since the
    // DOM has already been cleaned of padding wrappers by this point.
    const strippedInnerHTML = body.innerHTML

    const footerNormalized = footerStartPhrase ? normalizeText(footerStartPhrase) : null
    const footerFirstLineNormalized = footerStartPhrase ? normalizeText(footerStartPhrase.split(/\r?\n/)[0] ?? '') : null
    const boundary = findBoundaryElement(body, footerNormalized, footerFirstLineNormalized || null)

    if (!boundary) {
        return { visible: strippedInnerHTML, hidden: '' }
    }

    const hiddenHost = doc.createElement('div')
    extractHidden(body, boundary, hiddenHost)
    trimTrailingEmpty(body)

    const visible = body.innerHTML.trim()
    const hidden = hiddenHost.innerHTML.trim()

    // Detection mis-fired: the boundary left nothing visible. Rather than swap
    // (which would display the hidden block with no toggle to collapse it
    // back), fall back to the stripped (no empty-block) HTML so the user still
    // sees everything, just without our Gmail-style cut.
    if (!visible) {
        return { visible: strippedInnerHTML, hidden: '' }
    }

    return { visible, hidden }
}
